// Only chosen tracks live in the form. Search pages never replace submitted inputs.
export function initComposerCatalogTrackPicker(picker) {
  if (picker.dataset.pickerInitialized) return;
  picker.dataset.pickerInitialized = 'true';
  const find = key => picker.querySelector(`[data-picker-${key}]`);
  const selected = find('selected');
  const query = find('query');
  const results = find('results');
  const status = find('status');
  const previous = find('previous');
  const next = find('next');
  const retry = find('retry');
  let page = 1;
  let timer;
  let request;
  let generation = 0;
  // Keep five complete rows visible, including when long titles wrap on mobile.
  const fitLists = () => {
    [selected, results].forEach(list => {
      const height = [...list.children].slice(0, 5).reduce((total, row) => total + row.getBoundingClientRect().height, 0);
      if (height > 0) list.style.maxHeight = `${height}px`;
    });
  };
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(fitLists).observe(picker);
  const selectedInput = id => [...selected.querySelectorAll('input[type="checkbox"]')].find(input => input.value === String(id));
  const update = () => {
    const chosen = [...selected.querySelectorAll('input[type="checkbox"]:checked')];
    find('count').textContent = String(chosen.length);
    find('empty').hidden = chosen.length > 0;
    results.querySelectorAll('input[type="checkbox"]').forEach(input => { input.checked = !!selectedInput(input.value)?.checked; });
    fitLists();
  };
  const rowFor = (track, submitted) => {
    const row = document.createElement('label');
    row.className = 'catalog-track-picker__row';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.value = String(track.id);
    input.checked = submitted || !!selectedInput(track.id)?.checked;
    if (submitted) {
      input.name = picker.dataset.inputName;
      input.id = `${picker.dataset.idPrefix}_${track.id}`;
    }
    const label = document.createElement('span');
    label.textContent = `${track.title} · #${track.id}`;
    row.append(input, label);
    return row;
  };
  const cancel = () => {
    clearTimeout(timer);
    request?.abort();
    generation += 1;
  };
  const validQuery = () => query.value.trim().length >= 2 || /^\d+$/.test(query.value.trim());
  const search = async () => {
    cancel();
    results.replaceChildren();
    find('pagination').hidden = true;
    retry.hidden = true;
    if (!validQuery()) { status.textContent = picker.dataset.prompt; return; }
    const current = generation;
    request = new AbortController();
    const url = new URL(picker.dataset.searchUrl, window.location.href);
    url.searchParams.set('q', query.value.trim());
    url.searchParams.set('page', String(page));
    status.textContent = picker.dataset.loading;
    results.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: request.signal });
      if (!response.ok || response.redirected || !response.headers.get('content-type')?.includes('application/json')) throw new Error('Search unavailable');
      const data = await response.json();
      if (current !== generation) return;
      data.tracks.forEach(track => {
        const row = rowFor(track, false);
        row.querySelector('input').addEventListener('change', event => {
          const existing = selectedInput(track.id);
          if (existing) existing.checked = event.target.checked;
          else if (event.target.checked) selected.append(rowFor(track, true));
          update();
        });
        results.append(row);
      });
      fitLists();
      status.textContent = data.tracks.length ? `${picker.dataset.resultsLabel}: ${data.tracks.length}` : picker.dataset.noMatches;
      previous.disabled = page <= 1;
      next.disabled = !data.has_more;
      find('page').textContent = String(page);
      find('pagination').hidden = page === 1 && !data.has_more;
    } catch (error) {
      if (current !== generation || error.name === 'AbortError') return;
      status.textContent = picker.dataset.failed;
      retry.hidden = false;
    } finally {
      if (current === generation) results.removeAttribute('aria-busy');
    }
  };
  query.addEventListener('input', () => {
    cancel();
    page = 1;
    results.replaceChildren();
    results.removeAttribute('aria-busy');
    find('pagination').hidden = true;
    retry.hidden = true;
    status.textContent = validQuery() ? picker.dataset.loading : picker.dataset.prompt;
    if (validQuery()) timer = setTimeout(search, 250);
  });
  query.addEventListener('keydown', event => {
    if (event.key === 'Enter') { event.preventDefault(); page = 1; search(); }
  });
  previous.addEventListener('click', () => { page -= 1; search(); });
  next.addEventListener('click', () => { page += 1; search(); });
  retry.addEventListener('click', search);
  selected.addEventListener('change', update);
  find('search').hidden = false;
  query.disabled = false;
  status.textContent = picker.dataset.prompt;
  update();
}
